import React, { ComponentType, useMemo, useRef } from "react";
import { useLocation, useNavigationType } from "react-router";

/** Navigation state for a view's own URL writes: the URL changes but the view stays mounted. */
export const SKIP_REMOUNT_STATE = { skipRemount: true } as const;

export interface RemountOnQueryChangeOptions {
  /**
   * Query parameters the view writes itself for UI state, such as whether a filter panel
   * is expanded. Changing these must not remount the view, or it would lose its filters.
   */
  ignore?: string[];
}

/**
 * Remounts the wrapped component whenever the URL query string changes.
 *
 * Views seed their filters from the query string once, into `useState` or a default
 * query. Following a link to the same route with different parameters (for example one
 * from Sentry while the page is already open) keeps the component mounted, so without a
 * remount the old filters would stay. Any parameter the view writes back to the URL
 * itself must be listed in `ignore`, or written with {@link SKIP_REMOUNT_STATE}, or every
 * change to it would remount the view.
 *
 * @param WrappedComponent - The view whose filters are seeded from the query string.
 * @param options - Parameters to leave out of the remount key.
 * @returns The view, keyed on the query string.
 *
 * @example
 * export default withRemountOnQueryChange(MyReport, { ignore: ['showAllFilters'] });
 */
export const withRemountOnQueryChange = <P extends object>(
  WrappedComponent: ComponentType<P>,
  { ignore = [] }: RemountOnQueryChangeOptions = {},
): ComponentType<P> => {
  const WithRemountOnQueryChange = (props: P) => {
    const { search, state } = useLocation();
    const navigationType = useNavigationType();
    const query = useMemo(() => {
      const params = new URLSearchParams(search);
      ignore.forEach((name) => params.delete(name));
      return params.toString();
    }, [search]);

    // Tracks the last query seen, including the view's own writes, so a link back to an
    // earlier URL still counts as a change.
    const mount = useRef({ query, generation: 0 });
    if (mount.current.query !== query) {
      // The marker stays on the history entry, so Back/Forward (POP) onto it must still reseed.
      const skipRemount = navigationType === 'REPLACE'
        && (state as { skipRemount?: boolean } | null)?.skipRemount === true;
      mount.current = {
        query,
        generation: skipRemount ? mount.current.generation : mount.current.generation + 1,
      };
    }

    return <WrappedComponent key={mount.current.generation} {...props} />;
  };

  WithRemountOnQueryChange.displayName =
    `withRemountOnQueryChange(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithRemountOnQueryChange;
};
